import { useState, useEffect, useCallback } from "react";
import API from "../../services/apinode";
import Navbar from '../../components/navbar/navbar'

const PLATAFORMA_CORES = {
  MERCADOLIVRE: "warning",
  MAGALU: "primary",
  B2W: "danger",
  LEROY: "success",
  BLING: "info",
  ZIPHUB: "secondary",
};

function normalizar(v) {
  return String(v ?? "").trim();
}

function makeKey(tabela, id_conta) {
  return `${normalizar(tabela)}::${normalizar(id_conta)}`;
}

function Badge({ texto }) {
  const cor = PLATAFORMA_CORES[texto?.toUpperCase()] || "dark";
  return (
    <span className={`badge bg-${cor} bg-opacity-75 text-white`} style={{ fontSize: 11 }}>
      {texto || "—"}
    </span>
  );
}

function Avatar({ nome }) {
  const ini = nome?.split(" ").slice(0, 2).map(p => p[0]).join("").toUpperCase() || "?";
  const cores = ["#0d6efd", "#198754", "#dc3545", "#fd7e14", "#6f42c1", "#0dcaf0"];
  const cor = cores[nome?.charCodeAt(0) % cores.length] || "#6c757d";
  return (
    <div style={{
      width: 38, height: 38, borderRadius: "50%", background: cor,
      display: "flex", alignItems: "center", justifyContent: "center",
      color: "#fff", fontWeight: 700, fontSize: 14, flexShrink: 0,
    }}>{ini}</div>
  );
}

function Alerta({ tipo, mensagem, onFechar }) {
  if (!mensagem) return null;
  return (
    <div className={`alert alert-${tipo} alert-dismissible fade show`} role="alert">
      <i className={`bi bi-${tipo === "success" ? "check-circle" : "exclamation-triangle"}-fill me-2`} />
      {mensagem}
      <button type="button" className="btn-close" onClick={onFechar} />
    </div>
  );
}

function useDados(conta) {
  const [funcionarios, setFuncionarios] = useState([]);
  const [contas, setContas] = useState({ parametros: [], parametros_mk: [] });
  const [acessos, setAcessos] = useState({});
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);

  useEffect(() => {
    if (!conta) return;

    async function carregar() {
      setCarregando(true);
      try {
        const [resFuncs, resContas] = await Promise.all([
          API.get(`/acesso/funcionarios/${conta}`),
          API.get(`/acesso/contas/${conta}`),
        ]);

        const funcs = resFuncs.data;
        const contasData = resContas.data;

        setFuncionarios(funcs);
        setContas(contasData);

        const pares = await Promise.all(
          funcs.map(f =>
            API.get(`/acesso/acessos/${conta}/${f.idfuncionario}`)
              .then(res => [f.idfuncionario, res.data])
          )
        );
        setAcessos(Object.fromEntries(pares));
      } catch (e) {
        console.error(e);
        const mensagem = e.response?.data?.message || e.message || "Erro ao carregar dados.";
        setErro(mensagem);
      } finally {
        setCarregando(false);
      }
    }

    carregar();
  }, [conta]);

  const salvarAcessos = useCallback(async (idFuncionario, novasContas) => {
    try {
      await API.put(`/acesso/acessos/sincronizar/${conta}`, {
        idFuncionario,
        contas: novasContas
      });
      const response = await API.get(`/acesso/acessos/${conta}/${idFuncionario}`);
      setAcessos(prev => ({ ...prev, [idFuncionario]: response.data }));
    } catch (e) {
      const mensagem = e.response?.data?.message || e.message || "Erro ao salvar acessos.";
      throw new Error(mensagem);
    }
  }, [conta]);

  return { funcionarios, contas, acessos, carregando, erro, salvarAcessos };
}

function ModalAcessos({ funcionario, contas, acessosAtuais, onSalvar, onFechar }) {
  // Normaliza as chaves vindas da API para garantir comparação correta
  const [selecionadas, setSelecionadas] = useState(
    new Set(acessosAtuais.map(a => makeKey(a.tabela, a.id_conta)))
  );
  const [salvando, setSalvando] = useState(false);
  const [sucesso, setSucesso] = useState(false);
  const [erro, setErro] = useState(null);

  // Debug: mostra no console o que veio da API e o que foi montado no Set
  useEffect(() => {
    console.log("[AcessoConta] acessosAtuais recebidos:", acessosAtuais);
    console.log("[AcessoConta] selecionadas iniciais:", [...selecionadas]);
  }, []);

  const toggle = (tabela, id_conta) => {
    const key = makeKey(tabela, id_conta);
    setSelecionadas(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleSalvar = async () => {
    setSalvando(true);
    setErro(null);
    try {
      const novasContas = [...selecionadas].map(k => {
        const [tabela, idConta] = k.split("::");
        return { idConta, tabela };
      });
      await onSalvar(funcionario.idfuncionario, novasContas);
      setSucesso(true);
      setTimeout(() => { setSucesso(false); onFechar(); }, 1200);
    } catch (e) {
      setErro(e.message || "Erro ao salvar acessos.");
    } finally {
      setSalvando(false);
    }
  };

  const grupos = [
    { label: "ZipHub — PARAMETROS", lista: contas.parametros },
    { label: "Marketplaces — PARAMETROS_MK", lista: contas.parametros_mk },
  ];

  return (
    <div className="modal show d-block" style={{ background: "rgba(0,0,0,.45)" }}>
      <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content border-0 shadow-lg">

          <div className="modal-header border-bottom bg-light">
            <div className="d-flex align-items-center gap-3">
              <Avatar nome={funcionario.nomefuncionario} />
              <div>
                <h5 className="modal-title mb-0">{funcionario.nomefuncionario}</h5>
                <small className="text-muted">{funcionario.email}</small>
              </div>
            </div>
            <button className="btn-close" onClick={onFechar} />
          </div>

          <div className="modal-body p-4">
            {erro && <Alerta tipo="danger" mensagem={erro} onFechar={() => setErro(null)} />}

            {grupos.map(({ label, lista }) => (
              <div key={label} className="mb-4">
                <p className="fw-semibold text-muted mb-2"
                  style={{ fontSize: 13, textTransform: "uppercase", letterSpacing: 1 }}>
                  {label}
                </p>
                <div className="row g-2">
                  {lista.length === 0 && (
                    <div className="col-12 text-muted text-center py-3" style={{ fontSize: 13 }}>
                      Nenhuma conta cadastrada
                    </div>
                  )}
                  {lista.map(c => {
                    // Usa a mesma função makeKey para garantir que a chave é idêntica
                    const key = makeKey(c.tabela, c.id_conta);
                    const ativo = selecionadas.has(key);

                    return (
                      <div className="col-md-6" key={key}>
                        <div
                          className={`d-flex align-items-center gap-3 p-3 rounded-3 border ${ativo ? "border-primary bg-primary bg-opacity-10" : "border-light-subtle"
                            }`}
                          style={{ cursor: "pointer", transition: "all .15s" }}
                          onClick={() => toggle(c.tabela, c.id_conta)}
                        >
                          <input
                            type="checkbox"
                            className="form-check-input mt-0 flex-shrink-0"
                            checked={ativo}
                            onChange={() => toggle(c.tabela, c.id_conta)}
                            onClick={e => e.stopPropagation()} // evita double toggle
                            style={{ width: 18, height: 18 }}
                          />
                          <div className="flex-grow-1 overflow-hidden">
                            <div className="d-flex align-items-center gap-2">
                              <span className="fw-semibold text-truncate" style={{ fontSize: 14 }}>
                                {c.nome_conta}
                              </span>
                              {c.ativo === "N" && (
                                <span className="badge bg-danger bg-opacity-50 text-danger" style={{ fontSize: 10 }}>
                                  Inativa
                                </span>
                              )}
                            </div>
                            <div className="d-flex align-items-center gap-2 mt-1">
                              <Badge texto={c.plataforma} />
                              <span className="text-muted" style={{ fontSize: 12 }}>ID: {c.id_conta}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="modal-footer border-top bg-light">
            <span className="me-auto text-muted" style={{ fontSize: 13 }}>
              {selecionadas.size} conta(s) selecionada(s)
            </span>
            <button className="btn btn-outline-secondary" onClick={onFechar} disabled={salvando}>
              Cancelar
            </button>
            <button className="btn btn-primary px-4" onClick={handleSalvar} disabled={salvando}>
              {sucesso ? (
                <><i className="bi bi-check-lg me-1" />Salvo!</>
              ) : salvando ? (
                <><span className="spinner-border spinner-border-sm me-2" />Salvando…</>
              ) : "Salvar acessos"}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}

export default function AcessoConta({ conta }) {
  const { funcionarios, contas, acessos, carregando, erro, salvarAcessos } = useDados(conta);

  const [busca, setBusca] = useState("");
  const [filtroAtivo, setFiltro] = useState("todos");
  const [editando, setEditando] = useState(null);
  const [visualizando, setVis] = useState(null);
  const [alerta, setAlerta] = useState(null);

  const todasContas = [...contas.parametros, ...contas.parametros_mk];
  const getAcessos = id => acessos[id] || [];
  const getNomeConta = idConta => todasContas.find(c => normalizar(c.id_conta) === normalizar(idConta))?.nome_conta || idConta;

  const filtrados = funcionarios.filter(f => {
    const q = busca.toLowerCase();
    const ok = f.nomefuncionario.toLowerCase().includes(q) ||
      f.email?.toLowerCase().includes(q) ||
      f.usuario?.toLowerCase().includes(q);
    if (filtroAtivo === "ativos") return ok && f.ativo === "S";
    if (filtroAtivo === "inativos") return ok && f.ativo === "N";
    if (filtroAtivo === "admin") return ok && f.admin === "SIM";
    return ok;
  });

  const handleSalvar = async (idFuncionario, novasContas) => {
    await salvarAcessos(idFuncionario, novasContas);
    setAlerta({ tipo: "success", mensagem: "Acessos salvos com sucesso!" });
    setTimeout(() => setAlerta(null), 3000);
  };

  if (carregando) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: "60vh" }}>
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" style={{ width: 48, height: 48 }} />
          <p className="text-muted">Carregando dados…</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Navbar />

      <div className="container-fluid py-4 px-4">

        {alerta && <Alerta tipo={alerta.tipo} mensagem={alerta.mensagem} onFechar={() => setAlerta(null)} />}
        {erro && <Alerta tipo="danger" mensagem={erro} onFechar={() => { }} />}

        <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-3">
          <div>
            <h4 className="mb-0 fw-bold">
              <i className="bi bi-shield-lock-fill text-primary me-2" />
              Controle de Acesso — Funcionários
            </h4>
            <p className="text-muted mb-0 mt-1" style={{ fontSize: 14 }}>
              Gerencie quais contas ZipHub e Marketplaces cada funcionário pode acessar
            </p>
          </div>
          <div className="d-flex gap-2">
            <span className="badge bg-primary bg-opacity-10 text-primary px-3 py-2" style={{ fontSize: 13 }}>
              {funcionarios.filter(f => f.ativo === "S").length} funcionários ativos
            </span>
            <span className="badge bg-secondary bg-opacity-10 text-secondary px-3 py-2" style={{ fontSize: 13 }}>
              {todasContas.length} contas
            </span>
          </div>
        </div>

        <div className="card border-0 shadow-sm mb-4">
          <div className="card-body py-3">
            <div className="d-flex flex-wrap gap-3 align-items-center">
              <div className="input-group" style={{ maxWidth: 320 }}>
                <span className="input-group-text bg-white border-end-0">
                  <i className="bi bi-search text-muted" />
                </span>
                <input
                  type="text"
                  className="form-control border-start-0 ps-0"
                  placeholder="Buscar funcionário…"
                  value={busca}
                  onChange={e => setBusca(e.target.value)}
                />
              </div>
              <div className="btn-group">
                {[
                  { key: "todos", label: "Todos" },
                  { key: "ativos", label: "Ativos" },
                  { key: "inativos", label: "Inativos" },
                  { key: "admin", label: "Admins" },
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    className={`btn btn-sm ${filtroAtivo === key ? "btn-primary" : "btn-outline-secondary"}`}
                    onClick={() => setFiltro(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="card border-0 shadow-sm">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light border-bottom">
                <tr>
                  <th className="ps-4" style={{ width: 40 }}>#</th>
                  <th>Funcionário</th>
                  <th>Usuário</th>
                  <th>Perfil</th>
                  <th>Status</th>
                  <th>Contas com acesso</th>
                  <th className="text-end pe-4">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filtrados.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center py-5 text-muted">
                      <i className="bi bi-person-x fs-2 d-block mb-2" />
                      Nenhum funcionário encontrado
                    </td>
                  </tr>
                )}
                {filtrados.map((f, i) => {
                  const ac = getAcessos(f.idfuncionario);
                  return (
                    <tr key={f.idfuncionario} style={{ opacity: f.ativo === "N" ? 0.55 : 1 }}>
                      <td className="ps-4 text-muted" style={{ fontSize: 13 }}>{i + 1}</td>
                      <td>
                        <div className="d-flex align-items-center gap-3">
                          <Avatar nome={f.nomefuncionario} />
                          <div>
                            <div className="fw-semibold" style={{ fontSize: 14 }}>{f.nomefuncionario}</div>
                            <div className="text-muted" style={{ fontSize: 12 }}>{f.email}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <code className="bg-light px-2 py-1 rounded" style={{ fontSize: 12 }}>
                          {f.usuario}
                        </code>
                      </td>
                      <td>
                        {f.admin === "SIM" ? (
                          <span className="badge bg-danger bg-opacity-10 text-danger">
                            <i className="bi bi-shield-fill me-1" />Admin
                          </span>
                        ) : (
                          <span className="badge bg-secondary bg-opacity-10 text-secondary">Operador</span>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${f.ativo === "S"
                          ? "bg-success bg-opacity-10 text-success"
                          : "bg-danger bg-opacity-10 text-danger"}`}>
                          {f.ativo === "S" ? "Ativo" : "Inativo"}
                        </span>
                      </td>
                      <td>
                        {ac.length === 0 ? (
                          <span className="text-muted" style={{ fontSize: 13 }}>
                            <i className="bi bi-ban me-1" />Sem acesso
                          </span>
                        ) : (
                          <div className="d-flex flex-wrap gap-1">
                            {ac.slice(0, 3).map(a => (
                              <span
                                key={`${a.tabela}${a.id_conta}`}
                                className="badge bg-light text-dark border"
                                style={{ fontSize: 11 }}
                              >
                                {getNomeConta(a.id_conta)}
                              </span>
                            ))}
                            {ac.length > 3 && (
                              <button
                                className="badge bg-primary bg-opacity-10 text-primary border-0"
                                style={{ cursor: "pointer", fontSize: 11 }}
                                onClick={() => setVis(f)}
                              >
                                +{ac.length - 3} mais
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="text-end pe-4">
                        <div className="d-flex gap-2 justify-content-end">
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            title="Ver todos os acessos"
                            onClick={() => setVis(f)}
                          >
                            <i className="bi bi-eye" />
                          </button>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            title="Editar acessos"
                            onClick={() => setEditando(f)}
                            disabled={f.ativo === "N"}
                          >
                            <i className="bi bi-pencil-square" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="card-footer bg-white border-top py-2 px-4">
            <span className="text-muted" style={{ fontSize: 13 }}>
              Exibindo {filtrados.length} de {funcionarios.length} funcionários
            </span>
          </div>
        </div>

        {editando && (
          <ModalAcessos
            funcionario={editando}
            contas={contas}
            acessosAtuais={getAcessos(editando.idfuncionario)}
            onSalvar={handleSalvar}
            onFechar={() => setEditando(null)}
          />
        )}

        {visualizando && (
          <div className="modal show d-block" style={{ background: "rgba(0,0,0,.45)" }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content border-0 shadow-lg">
                <div className="modal-header bg-light border-bottom">
                  <div className="d-flex align-items-center gap-3">
                    <Avatar nome={visualizando.nomefuncionario} />
                    <div>
                      <h6 className="modal-title mb-0">{visualizando.nomefuncionario}</h6>
                      <small className="text-muted">Acessos configurados</small>
                    </div>
                  </div>
                  <button className="btn-close" onClick={() => setVis(null)} />
                </div>
                <div className="modal-body p-4">
                  {getAcessos(visualizando.idfuncionario).length === 0 ? (
                    <div className="text-center text-muted py-4">
                      <i className="bi bi-ban fs-2 d-block mb-2" />
                      Nenhum acesso configurado
                    </div>
                  ) : (
                    <div className="list-group list-group-flush">
                      {getAcessos(visualizando.idfuncionario).map(a => {
                        const c = todasContas.find(x => normalizar(x.id_conta) === normalizar(a.id_conta));
                        return (
                          <div
                            key={`${a.tabela}${a.id_conta}`}
                            className="list-group-item px-0 d-flex align-items-center gap-3"
                          >
                            <i className="bi bi-check-circle-fill text-success" />
                            <div>
                              <div className="fw-semibold" style={{ fontSize: 14 }}>
                                {c?.nome_conta || a.id_conta}
                              </div>
                              <div className="d-flex gap-2 mt-1">
                                <Badge texto={c?.plataforma} />
                                <span className="text-muted" style={{ fontSize: 11 }}>{a.tabela}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="modal-footer bg-light border-top">
                  <button className="btn btn-outline-secondary" onClick={() => setVis(null)}>
                    Fechar
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => { setVis(null); setEditando(visualizando); }}
                  >
                    Editar acessos
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </>
  );
}